import type { ErasureReport, ErasureStatus } from "@/types/erasure";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardHeader } from "./ui/card";

const statusConfig: Record<
  ErasureStatus,
  { label: string; className: string }
> = {
  confirmed: {
    label: "Bekräftad",
    className:
      "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-300 dark:border-green-800",
  },
  pending: {
    label: "Väntar",
    className:
      "bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border-amber-300 dark:border-amber-800",
  },
  in_progress: {
    label: "Pågår",
    className:
      "bg-cyan-100 dark:bg-cyan-900/30 text-cyan-800 dark:text-cyan-300 border-cyan-300 dark:border-cyan-800",
  },
  failed: {
    label: "Misslyckades",
    className:
      "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-300 dark:border-red-800",
  },
};

interface ErasureTableProps {
  report: ErasureReport;
  sample?: boolean;
}

export function ErasureTable({ report, sample = false }: ErasureTableProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <span className="font-medium">Echorai – Raderingsbevis</span>
        {sample && (
          <Badge className="bg-cyan-100 dark:bg-cyan-900/30 text-cyan-800 dark:text-cyan-300 border-cyan-300 dark:border-cyan-800">
            Exempel
          </Badge>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col border bg-background rounded-xl p-2">
            <span className="text-xs font-light text-muted-foreground">
              Tjänst
            </span>
            <span>{report.service}</span>
          </div>
          <div className="flex flex-col border bg-background rounded-xl p-2">
            <span className="text-xs font-light text-muted-foreground">
              Rapport-ID
            </span>
            <span className="font-mono text-sm">{report.reportId}</span>
          </div>
        </div>
        <div className="overflow-x-auto rounded-md border bg-background">
          <Table className="text-xs">
            <TableHeader className="bg-muted">
              <TableRow>
                <TableHead>Webbplats</TableHead>
                <TableHead>Begärd</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Verifiering</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.requests.map((request) => {
                const config = statusConfig[request.status];
                return (
                  <TableRow key={request.id}>
                    <TableCell className="font-medium">
                      {request.site}
                    </TableCell>
                    <TableCell>{request.requestedAt}</TableCell>
                    <TableCell>
                      <Badge className={config.className}>{config.label}</Badge>
                    </TableCell>
                    <TableCell>
                      {request.verificationUrl ? (
                        <a
                          href={request.verificationUrl}
                          className="text-cyan-600 dark:text-cyan-400 hover:underline"
                        >
                          Skärmdump
                        </a>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
